import { Link, Navigate, useSearch } from "@tanstack/react-router";
import { ROUTES } from "@/app/routes";
import { isAlumniOnly } from "@/shared/auth/alumni-access";
import { useAuth } from "@/shared/auth/auth.context";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";

interface RedirectWorkspace {
  id: string;
  slug: string;
  isDemo?: boolean;
}

/// `preferredId` is the shore's pick (the Google callback's `?workspace=`). It
/// is honoured only when it names one of this person's own workspaces, so a
/// stale or hand-edited id falls back to the usual choice instead of a 404.
export function pickDefaultWorkspace<T extends RedirectWorkspace>(
  workspaces: ReadonlyArray<T>,
  preferredId?: string,
): T | undefined {
  const preferred = preferredId
    ? workspaces.find((workspace) => workspace.id === preferredId)
    : undefined;
  return preferred ?? workspaces.find((workspace) => !workspace.isDemo) ?? workspaces[0];
}

/**
 * Root `/` for authenticated users, redirects to the first workspace's home.
 * Anonymous users see a "sign in" prompt (the auth provider already 401'd
 * `/auth/me` so we know we're not authenticated).
 *
 * When a user has no workspaces (edge case, first signup mid-flight), we
 * show a prompt to create one. Once they do, AuthProvider refetches and the
 * redirect kicks in.
 */
export function WorkspaceRedirectRoute() {
  const auth = useAuth();
  // strict:false, `/` may be reached with `?firstRun=1` (signup), with
  // `?workspace=<id>` (Google sign-in) or with no search (login/verify).
  // Forward `firstRun` so the first-run banner shows.
  const { firstRun, workspace } = useSearch({ strict: false }) as {
    firstRun?: number;
    workspace?: string;
  };

  if (auth.isLoading) {
    return null;
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-6 py-16 text-center">
        <h1 className="font-display text-2xl font-semibold">Welcome to Pulsewise</h1>
        <p className="text-sm text-text-secondary">Sign in to see your workspaces.</p>
        <div className="flex justify-center gap-2">
          <Button asChild>
            <Link to="/login" search={{}}>
              Sign in
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/signup" search={{}}>
              Sign up
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  // PAYD-FR-042 (H-06). A former employee has no workspace and cannot make
  // one: telling them to "create one from the workspace switcher" (the branch
  // below) would be advice they cannot follow, on a screen that also has no
  // switcher. Send them to their own records instead.
  if (isAlumniOnly(auth.workspaces, auth.alumniMemberships)) {
    return <Navigate to={ROUTES.alumni.path} replace />;
  }

  const first = pickDefaultWorkspace(auth.workspaces, workspace);
  if (!first) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-4 px-6 py-16 text-center">
        <Card>
          <CardContent className="py-8">
            <p className="text-sm font-medium">You don't have any workspaces yet.</p>
            <p className="mt-1 text-sm text-text-tertiary">
              Create one from the workspace switcher in the sidebar.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Navigate
      to={ROUTES.workspace.home.path}
      params={{ workspaceSlug: first.slug }}
      search={{ firstRun }}
      replace
    />
  );
}
